<template>
    <nav class="navbar navbar-dark justify-content-between fixed-top flex-md-nowrap p-0 bg-light">
        <router-link :to="url" class="shadow-none navbar-brand me-0 col-12 col-md-3 col-lg-2">
            <img class="img-logo display-sid-min" src="/images/logo-lead-extend.png" style="width: 160px" v-if="!isDevelopement()" />
            <img class="img-logo display-sid-min" src="/images/logo-lead-extend.png" style="width: 160px" v-else />
        </router-link>
        <div class="default-topbar-actions">
            <ShowNotificationsComponent
                v-if="isCourier"
                :showNotifications="notifications.show"
                :module_type="MODULE_TYPE_CLICK_AND_COLLECT.value"
                @open="notifications.show = true"
                @close="notifications.show = false" />

            <div v-if="currentUser" class="default-account-menu-wrapper">
                <button
                    ref="accountButton"
                    class="btn btn-sm p-0 default-account-button me-3"
                    type="button"
                    :class="{ 'mt-1': isCourier, active: accountButtonActive }"
                    :aria-expanded="accountButtonActive ? 'true' : 'false'"
                    aria-haspopup="menu"
                    aria-controls="account-panel"
                    :aria-label="accountButtonLabel"
                    v-tooltip="accountTooltip"
                    @click.stop="handleAccountButtonClick">
                    <span class="default-account-avatar">{{ userInitials }}</span>
                    <span class="default-account-name">{{ userDisplayName }}</span>
                    <feather type="chevron-down" class="default-account-chevron"></feather>
                </button>
                <div v-if="usesAccountDropdown && accountMenuOpen" class="default-account-dropdown" role="menu">
                    <button data-test-id="btn-logout" type="button" class="default-account-dropdown-item" role="menuitem" @click="logout">
                        <feather type="log-out" />
                        <span>{{ $tl("labels.routes.disconnect") }}</span>
                    </button>
                </div>
            </div>
            <a
                ref="logoutButton"
                v-else
                data-test-id="btn-logout"
                class="logout-default"
                href="#"
                @click.prevent="logout"
                v-tooltip="getTooltip('Déconnexion')">
                <feather type="log-out" class="feather-22 text-danger me-3" :class="{ 'mt-1': isCourier }"></feather>
            </a>
        </div>
    </nav>
</template>

<script>
import RolesEnum from "../../mixins/enums/RolesEnum";
import ModuleTypesEnum from "../../mixins/enums/ModuleTypesEnum";
import ShowNotificationsComponent from "../../components/notifications/ShowNotificationsComponent.vue";
import { Tooltip } from "bootstrap";

export default {
    name: "topbar",
    props: {
        url: {
            default: () => {
                return { name: "home" };
            },
        },
        user: {
            default: null,
        },
        entries: {
            default: () => [],
        },
        accountPanelOpen: {
            default: false,
            type: Boolean,
        },
    },
    mixins: [RolesEnum, ModuleTypesEnum],
    data() {
        return {
            notifications: {
                show: false,
            },
            accountMenuOpen: false,
        };
    },
    mounted() {
        document.addEventListener("click", this.closeAccountMenu);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeAccountMenu);
        this.hideAccountTooltips();
    },
    methods: {
        closeAccountMenu() {
            this.accountMenuOpen = false;
        },
        hideAccountTooltips() {
            for (const element of [this.$refs.accountButton, this.$refs.logoutButton]) {
                if (element) {
                    Tooltip.getInstance(element)?.hide();
                }
            }
        },
        handleAccountButtonClick(event) {
            this.hideAccountTooltips();
            if (event && event.currentTarget) {
                event.currentTarget.blur();
            }

            if (this.usesAccountDropdown) {
                this.accountMenuOpen = !this.accountMenuOpen;
                return;
            }

            this.$emit("toggle-account-panel");
        },
        logout() {
            this.hideAccountTooltips();

            axios
                .post("/logout")
                .then((response) => {
                    window.location.replace(response.data.redirect_url || response.headers.location || "/login");
                })
                .catch(() => {});
        },
    },
    watch: {
        $route() {
            this.hideAccountTooltips();
            this.closeAccountMenu();
        },
    },
    computed: {
        currentUser() {
            if (this.user) {
                return this.user;
            }

            if (!this.$store.getters["users/id"]) {
                return null;
            }

            return {
                firstname: this.$store.getters["users/firstName"],
                lastname: this.$store.getters["users/lastName"],
                email: this.$store.getters["users/email"],
            };
        },
        userDisplayName() {
            if (!this.currentUser) {
                return this.$tl("labels.myAccount");
            }

            const name = [this.currentUser.firstname, this.currentUser.lastname]
                .filter((part) => typeof part === "string" && part.trim() !== "")
                .join(" ");

            return name || this.currentUser.email || this.$tl("labels.myAccount");
        },
        userInitials() {
            const names = [this.currentUser && this.currentUser.firstname, this.currentUser && this.currentUser.lastname].filter(
                (part) => typeof part === "string" && part.trim() !== ""
            );

            if (names.length > 0) {
                return names
                    .slice(0, 2)
                    .map((part) => part.trim().charAt(0).toUpperCase())
                    .join("");
            }

            const fallback = this.currentUser && typeof this.currentUser.email === "string" ? this.currentUser.email : this.$tl("labels.myAccount");
            return fallback.trim().charAt(0).toUpperCase();
        },
        isAdmin() {
            return this.$store.getters["users/role"] === this.ROLE_ADMIN.value;
        },
        isCourier() {
            return this.$store.getters["users/role"] === this.ROLE_COURIER.value;
        },
        usesAccountDropdown() {
            return this.isAdmin || this.isCourier;
        },
        accountButtonLabel() {
            return this.usesAccountDropdown ? "Ouvrir le menu du compte" : "Ouvrir le panneau du compte";
        },
        accountButtonActive() {
            return this.usesAccountDropdown ? this.accountMenuOpen : this.accountPanelOpen;
        },
        accountTooltip() {
            return this.getTooltip("Mon compte", { triggers: ["hover"] });
        },
    },
    components: {
        ShowNotificationsComponent,
    },
};
</script>

<style lang="scss" scoped>
.default-topbar-actions {
    display: flex;
    align-items: center;
}

.default-account-menu-wrapper {
    position: relative;
    display: inline-flex;
}

.default-account-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    max-width: 240px;
    min-height: 36px;
    padding: 3px 10px 3px 4px !important;
    border-radius: 999px;
    transition:
        background-color 0.18s ease,
        box-shadow 0.18s ease;
}

.default-account-button:hover,
.default-account-button.active {
    background: #ffffff;
    box-shadow: 0 8px 20px rgba(8, 62, 96, 0.12);
}

.default-account-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    color: #ffffff;
    font-size: 11px;
    font-weight: 800;
    background: #083e60;
    border-radius: 999px;
}

.default-account-name {
    overflow: hidden;
    color: #243041;
    font-size: 13px;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.default-account-chevron {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    color: #7a8796;
    transition: transform 0.18s ease;
}

.default-account-button.active .default-account-chevron {
    transform: rotate(180deg);
}

.default-account-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 12px;
    z-index: 1100;
    min-width: 190px;
    padding: 6px;
    background: #ffffff;
    border: 1px solid rgba(8, 62, 96, 0.1);
    border-radius: 12px;
    box-shadow: 0 18px 44px rgba(8, 62, 96, 0.16);
}

.default-account-dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 38px;
    padding: 0 10px;
    color: #d6454b;
    font-size: 13px;
    font-weight: 800;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 9px;
    transition:
        background-color 0.18s ease,
        color 0.18s ease;
}

.default-account-dropdown-item:hover,
.default-account-dropdown-item:focus {
    color: #b42328;
    background: #fff1f1;
    outline: none;
}

.default-account-dropdown-item .feather {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
}

@media (max-width: 575px) {
    .default-account-button {
        max-width: 46px;
        padding-right: 4px !important;
    }

    .default-account-name,
    .default-account-chevron {
        display: none;
    }
}
</style>
